import React from 'react';
import { TrendingUp, Activity, AlertCircle, CheckCircle } from 'lucide-react';

const DataStreamPanel = ({ streams = [] }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Activity className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'error':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffSeconds = Math.floor((now - date) / 1000);
    
    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
    if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
    return date?.toLocaleDateString();
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Live Data Streams</h2>
          <p className="text-sm text-gray-600 mt-1">
            Real-time ingestion rates and processing metrics
          </p>
        </div>
        <Activity className="w-6 h-6 text-blue-600" />
      </div>
      <div className="space-y-4">
        {streams?.map((stream, index) => (
          <div 
            key={index}
            className={`p-4 rounded-lg border ${getStatusColor(stream?.status)}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                {getStatusIcon(stream?.status)}
                <div>
                  <h3 className="font-semibold text-gray-900">{stream?.platform}</h3>
                  <p className="text-xs text-gray-600">
                    Last update: {formatTimestamp(stream?.lastUpdate)}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">
                  {stream?.throughput}
                </div>
                <p className="text-xs text-gray-600">items/hour</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white bg-opacity-50 rounded p-3">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-blue-600" />
                  <span className="text-xs text-gray-600">Throughput</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {stream?.throughput} <span className="text-xs font-normal">items/hr</span>
                </p>
              </div>

              <div className="bg-white bg-opacity-50 rounded p-3">
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <span className="text-xs text-gray-600">Error Rate</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {stream?.errorRate}%
                </p>
              </div>

              <div className="bg-white bg-opacity-50 rounded p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Activity className="w-4 h-4 text-purple-600" />
                  <span className="text-xs text-gray-600">Latency</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {stream?.latency} <span className="text-xs font-normal">ms</span>
                </p>
              </div>
            </div>

            {/* Progress Bar for Throughput */}
            <div className="mt-3">
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>Processing Capacity</span>
                <span>{Math.min(100, Math.floor((stream?.throughput / 250) * 100))}%</span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-500 ${
                    stream?.throughput > 200 ? 'bg-green-500' :
                    stream?.throughput > 100 ? 'bg-blue-500' :
                    stream?.throughput > 0 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.min(100, Math.floor((stream?.throughput / 250) * 100))}%` }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Summary Footer */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-gray-900">
              {streams?.reduce((sum, s) => sum + s?.throughput, 0)}
            </p>
            <p className="text-xs text-gray-600">Total Throughput</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">
              {streams?.filter(s => s?.status === 'healthy')?.length}
            </p>
            <p className="text-xs text-gray-600">Healthy Streams</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">
              {(streams?.reduce((sum, s) => sum + s?.errorRate, 0) / streams?.length)?.toFixed(1)}%
            </p>
            <p className="text-xs text-gray-600">Avg Error Rate</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataStreamPanel;